import { Repeater, types } from "react-bricks/frontend";
import styled from "styled-components";
import { color } from "@styles";
import { TimelineItemProps } from "./TimelineItem";
import { restrictedBackgroundColorProp } from "../utils/backgroundColor";
import {
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";
import TextBlock from "../common/TextBlock";

export interface TimelineProps {
  backgroundColor?: types.IColor;
  items: types.RepeaterItems<TimelineItemProps>;
  paddingVertical?: VerticalPadding;
}

export const Timeline: types.Brick<TimelineProps> = ({
  backgroundColor,
  items = [],
  paddingVertical,
}) => {
  return (
    <VerticalPaddingWrapper
      paddingVertical={paddingVertical}
      backgroundColor={backgroundColor?.color}
    >
      <Wrapper backgroundColor={backgroundColor?.color}>
        <TitleWrapper>
          <TextBlock
            propName="title"
            placeholder="From insurance setup to team rewards fast"
            renderBlock={({ children }) => <>{children}</>}
          />
        </TitleWrapper>
        <TimelineWrapper>
          {items.length > 1 && <DottedLine />}
          <Repeater propName="items" items={items} renderItemWrapper={(item) => <>{item}</>} />
        </TimelineWrapper>
      </Wrapper>
    </VerticalPaddingWrapper>
  );
};

Timeline.schema = {
  name: "timeline",
  label: "Timeline",
  category: "Timeline Components",
  previewImageUrl: "/static/bricks/timeline.png",
  repeaterItems: [
    {
      name: "items",
      itemType: "timeline-item",
      itemLabel: "Item",
      min: 1,
      max: 6,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    backgroundColor: { color: color.white },
    paddingVertical: "s",
    items: [
      {
        iconSrc: {
          src: "/static/icons/Star.svg",
        },
        text: "Choose your insurance plan",
      },
      {
        iconSrc: {
          src: "/static/icons/AdvisorsTimeline.svg",
        },
        text: "Speak with an expert",
      },
      {
        iconSrc: {
          src: "/static/icons/Success.svg",
        },
        text: "Setup the policy",
      },
      {
        iconSrc: {
          src: "/static/icons/SmartphoneTimeline.svg",
        },
        text: "Team downloads the app",
      },
      {
        iconSrc: {
          src: "/static/icons/OpenChest.svg",
        },
        text: "Team earns rewards for being active",
      },
      {
        iconSrc: {
          src: "/static/icons/Performance.svg",
        },
        text: "85% productivity increase*",
      },
    ],
    title: "From insurance setup to team rewards fast",
  }),
  // Define side edit props
  sideEditProps: [verticalPadSideProp, restrictedBackgroundColorProp],
};

const MOBILE_BREAKPOINT = `@media only screen and (max-width: 53rem)`;

const Wrapper = styled.section<{
  backgroundColor: string;
}>`
  background: ${(p) => p.backgroundColor || color.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TimelineWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  gap: 1.5rem;

  ${MOBILE_BREAKPOINT} {
    flex-direction: column;
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const DottedLine = styled.div`
  position: absolute;
  width: calc(100% - 7.2rem);
  top: 1.25rem;
  left: 3.6rem;
  border-top: 2px dashed #e6e6e9;
  transform: translateY(-50%);
  z-index: 1;

  ${MOBILE_BREAKPOINT} {
    height: calc(100% - 3.6rem);
    width: 1px;
    border-left: 2px dashed #e6e6e9;
    border-top: none;
    top: 1.8rem;
    left: 2.25rem;
    transform: translateX(-50%);
  }
`;

const TitleWrapper = styled.div`
  text-align: center;
  color: ${color.P100};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
`;

export default Timeline;
